import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { ListPlansUseCase } from "../application/usecases/list-plans.usecase"
import { Plan } from "../application/domain/plan"
import { SectionHeader } from "./section-header"

function PricingSkeleton() {
  return (
    <>
      {[0, 1].map((i) => (
        <div
          key={i}
          className="rounded-3xl p-8 md:p-10 border bg-card border-white/5 animate-pulse"
        >
          <div className="h-6 w-24 rounded bg-white/10" />
          <div className="mt-4 flex items-baseline gap-2">
            <div className="h-12 w-28 rounded bg-white/10" />
            <div className="h-4 w-12 rounded bg-white/10" />
          </div>
          <ul className="mt-8 space-y-3">
            {[0, 1, 2].map((j) => (
              <li key={j} className="h-4 rounded bg-white/10" />
            ))}
          </ul>
          <div className="mt-10 h-12 w-full rounded-full bg-white/10" />
        </div>
      ))}
    </>
  )
}

export function Pricing() {
  const [plans, setPlans] = useState<Plan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchPlans = async () => {
      setIsLoading(true)
      setHasError(false)
      const result = await ListPlansUseCase.execute()
      if (cancelled) return
      setPlans(result.plans)
      setHasError(result.error)
      setIsLoading(false)
    }

    fetchPlans()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <section id="precos" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          eyebrow="Preços"
          title="Simples e transparente"
          subtitle="Comece grátis. Evolua quando precisar."
        />

        {hasError && !isLoading && (
          <p className="mt-16 text-center text-sm text-muted-foreground">
            Não foi possível carregar os planos. Tente novamente mais tarde.
          </p>
        )}

        <div className="mt-16 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {isLoading && <PricingSkeleton />}

          {!isLoading &&
            !hasError &&
            plans.map((p) => (
              <div
                key={p.id}
                className={`relative rounded-3xl p-8 md:p-10 border transition-all ${
                  p.highlight
                    ? "bg-card border-primary/40 glow-primary-sm"
                    : "bg-card border-white/5 hover:border-white/15"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-bold text-primary-foreground glow-primary-sm">
                    MAIS POPULAR
                  </span>
                )}
                <h3 className="text-xl font-bold">{p.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className={`text-5xl font-extrabold ${p.highlight ? "text-primary" : ""}`}>{p.price}</span>
                  <span className="text-muted-foreground">{p.period}</span>
                </div>
                <ul className="mt-8 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-bold">
                        ✓
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={p.href}
                  className={`mt-10 inline-flex w-full items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold transition-all ${
                    p.highlight
                      ? "bg-primary text-primary-foreground glow-primary-sm hover:scale-[1.02]"
                      : "border border-white/15 text-foreground hover:border-primary hover:text-primary"
                  }`}
                >
                  {p.cta}
                </Link>
              </div>
            ))}
        </div>
      </div>
    </section>
  )
}
